import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './styles/invite.css';
import { acceptEmployeeInvite, getInviteByToken, getClientInviteByToken, acceptClientInvite } from '../company-management/invitations/services/invitationsApi';
import { useAuth } from '../context/AuthContext';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from './staffAuthApi';
import { app } from './appAuth';
import { sendCustomVerificationEmail } from './api/emailVerificationApi';

/** Format raw digits as (555) 555-5555, max 10 digits */
function formatPhoneDisplay(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function InviteAcceptPage({ mockInvite = null }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwScore, setPwScore] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [invite, setInvite] = useState(null);
  const [kind, setKind] = useState('employee');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = searchParams.get('token') || '';

  useEffect(() => {
    // If test page provided a mock invite, use it and skip fetch
    if (mockInvite) {
      setInvite(mockInvite);
      setEmail(mockInvite.email || '');
      return;
    }
    if (!token) {
      setError('Invalid or missing invite token');
      return;
    }
    const urlKind = (searchParams.get('kind') || '').toLowerCase();
    if (urlKind === 'special-access') {
      navigate(`/app/special-access/accept?token=${encodeURIComponent(token)}`, { replace: true });
      return;
    }
    const load = async () => {
      try {
        // Try explicit kind first
        if (urlKind === 'client') {
          const data = await getClientInviteByToken(token);
          setInvite(data);
          setEmail(data.email || '');
          setKind('client');
          return;
        }
        // Try employee first, then fallback to client
        try {
          const dataEmp = await getInviteByToken(token);
          setInvite(dataEmp);
          setEmail(dataEmp.email || '');
          setKind('employee');
        } catch (e1) {
          const dataCli = await getClientInviteByToken(token);
          setInvite(dataCli);
          setEmail(dataCli.email || '');
          setKind('client');
        }
      } catch (e) {
        setError(e?.message || 'Invalid invite');
      }
    };
    if (token) load();
  }, [token, mockInvite, navigate, searchParams]);

  const handleAccept = async () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const auth = getAuth(app);

      // If a different user is logged in, block and ask to switch
      if (user && user.email && email && user.email.toLowerCase() !== email.toLowerCase()) {
        setError('You are signed in as a different user. Please sign out and sign in with the invited email.');
        return;
      }

      // Ensure we are authenticated as the invited email
      let currentUser = auth.currentUser;
      if (!currentUser) {
        try {
          const signIn = await signInWithEmailAndPassword(auth, email, password);
          currentUser = signIn.user;
        } catch (signInErr) {
          const created = await createUserWithEmailAndPassword(auth, email, password);
          currentUser = created.user;
        }
      }

      // If somehow current user email doesn't match, stop
      if (!currentUser || (currentUser.email && email && currentUser.email.toLowerCase() !== email.toLowerCase())) {
        setError('Authenticated user does not match the invited email.');
        return;
      }

      const idToken = await currentUser.getIdToken(/* forceRefresh */ true);

      // Send custom verification email (SendGrid, no-reply@neatschedule.com)
      try {
        await sendCustomVerificationEmail(idToken);
      } catch (verifyErr) {
        console.warn('Could not send verification email:', verifyErr);
      }

      if (kind === 'client') {
        await acceptClientInvite({ token, idToken, firstName, lastName });
      } else {
        await acceptEmployeeInvite({ token, idToken, firstName, lastName, phone });
      }
      // Give backend a moment to propagate profile/claims, then hard refresh to prevent loading loops
      await new Promise((r) => setTimeout(r, 1000));
      if (kind === 'client') {
        // Use slug/customUrl for portal redirect
        const companyUrl = invite?.slug || invite?.companyUrl || '';
        if (companyUrl) {
          window.location.replace(`/book/${companyUrl}/account`);
        } else {
          window.location.replace('/app');
        }
      } else {
        window.location.replace('/app/overview');
      }
    } catch (e) {
      setError(e?.message || 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    const confirmed = window.confirm('Are you sure you want to close? You can accept this invitation later from the email link.');
    if (!confirmed) return;
    // Try to close the window/tab if it was opened separately
    try {
      if (window.opener || window.top !== window.self) {
        window.close();
      }
      // Some browsers require replacing the current page before close
      window.open('', '_self');
      window.close();
    } catch (e) { /* ignore */ }

    // Fallbacks if close is blocked
    if (document.referrer && document.referrer !== '') {
      navigate(-1);
      return;
    }
    if (user) {
      navigate('/app/overview');
    } else {
      navigate('/app/login');
    }
  };

  // Simple password strength estimator: 0-4
  useEffect(() => {
    const value = password || '';
    let score = 0;
    if (value.length >= 6) score += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value) || value.length >= 12) score += 1;
    setPwScore(score);
  }, [password]);

  const pwLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore];
  const pwClass = ['weak', 'weak', 'fair', 'good', 'strong'][pwScore];

  return (
    <div className="invite-page">
      <div className="invite-card">
        <div className="invite-card__header">
          {invite?.companyLogoUrl ? (
            <img src={invite.companyLogoUrl} alt="Company Logo" className="invite-logo-img" />
          ) : (
            <div className="invite-logo">S</div>
          )}
          <div>
            <div className="invite-title">Accept Invitation</div>
            <div className="invite-subtitle">
              {kind === 'client'
                ? 'Join the client portal'
                : 'Join your team on Neat Schedule'}
            </div>
          </div>
        </div>
        <div className="invite-card__body" style={{ overflow: 'visible' }}>
          {invite && (
            <div className="invite-banner">
              {kind === 'client' ? (
                <>
                  You were invited to join <strong>{invite.companyName || 'this company'}</strong>'s client portal. <span className="invite-muted">({invite.email})</span>
                </>
              ) : (
                <>
                  You were invited to join <strong>{invite.companyName || 'this company'}</strong> as a <strong>{invite.role}</strong>. <span className="invite-muted">({invite.email})</span>
                </>
              )}
            </div>
          )}
          <div className="invite-field">
            <label className="invite-label">Email</label>
            <input
              className="invite-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={Boolean(invite?.email)}
            />
          </div>
          <div className="invite-field">
            <label className="invite-label">Password
              <span className="info-dot" title="How to reach Strong">
                i
                <span className="tooltip">
                  Use <b>12+ characters</b> with a mix of <b>upper/lowercase</b>, <b>numbers</b>, and <b>symbols</b>.
                </span>
              </span>
            </label>
            <div className="invite-input-wrap">
              <input className="invite-input" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" className="toggle-eye" onClick={() => setShowPw((s) => !s)}>{showPw ? 'Hide' : 'Show'}</button>
            </div>
            <div className="pw-meter">
              <div className="pw-meter__bars">
                {[0,1,2,3].map((i) => (
                  <div key={i} className={`pw-meter__bar ${pwScore > i ? `active ${pwClass}` : ''}`} />
                ))}
              </div>
              <div className="pw-meter__label">{pwLabel}</div>
            </div>
          </div>
          <div className="invite-field">
            <label className="invite-label">Re-enter Password</label>
            <div className="invite-input-wrap">
              <input
                className="invite-input"
                type={showPw2 ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={password && confirmPassword && password !== confirmPassword ? { borderColor: '#dc2626' } : undefined}
              />
              <button type="button" className="toggle-eye" onClick={() => setShowPw2((s) => !s)}>{showPw2 ? 'Hide' : 'Show'}</button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <div className="invite-error">Passwords do not match</div>
            )}
          </div>
          <div className="invite-row">
            <div className="invite-field">
              <label className="invite-label">First Name</label>
              <input className="invite-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="invite-field">
              <label className="invite-label">Last Name</label>
              <input className="invite-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          {kind === 'employee' && (
            <div className="invite-field">
              <label className="invite-label">Phone</label>
              <input
                className="invite-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneDisplay(e.target.value))}
                placeholder="(555) 555-5555"
              />
            </div>
          )}
          {error && <div className="invite-error">{error}</div>}
        </div>
        <div className="invite-card__footer">
          <div className="invite-actions">
            <button className="btn btn-outline" onClick={handleCancel}>Close</button>
            <button
              className="btn btn-primary"
              onClick={handleAccept}
              disabled={loading || !token || !email || !password || password !== confirmPassword}
            >
              {loading ? <span className="spinner" /> : null}
              {loading ? 'Joining...' : 'Join Company'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



